/**
 * Support Service
 * 
 * API service for in-app support ticket chat system.
 * Handles creating, listing, and managing support tickets with chat-style messages.
 */

import { api } from '../utils/api'

// Types
export type TicketCategory = 'bug' | 'missing_image' | 'feature_request' | 'support' | 'data_issue'
export type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed'
export type TicketPriority = 'low' | 'normal' | 'high'
export type TicketVisibility = 'user_only' | 'organization'

export interface TicketUser {
  id: string
  email: string
  firstName: string
  lastName: string
}

export interface Ticket {
  id: string
  organizationId: string
  createdByUserId: string
  category: TicketCategory
  status: TicketStatus
  priority: TicketPriority
  visibility: TicketVisibility
  title: string
  description: string | null
  context: Record<string, any>
  attachments: string[]
  lastMessageAt: string
  unreadByUser: boolean
  unreadByAdmin?: boolean
  createdAt: string
  updatedAt: string
  createdBy?: TicketUser
  _count?: {
    messages: number
  }
}

export interface TicketMessage {
  id: string
  ticketId: string
  authorUserId: string | null
  isAdminReply: boolean
  adminName?: string | null
  body: string
  attachments: string[]
  createdAt: string
  author?: TicketUser | null
}

export interface TicketEvent {
  id: string
  ticketId: string
  type: string
  fromStatus: TicketStatus | null
  toStatus: TicketStatus | null
  metadata: Record<string, any>
  createdAt: string
}

export interface TicketDetail extends Ticket {
  messages: TicketMessage[]
  events: TicketEvent[]
}

export interface CreateTicketData {
  category: TicketCategory
  priority?: TicketPriority
  visibility?: TicketVisibility
  title: string
  description?: string
  context?: Record<string, any>
  attachments?: string[]
}

export interface TicketListResponse {
  tickets: Ticket[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface TicketFilters {
  status?: TicketStatus
  category?: TicketCategory
  priority?: TicketPriority
  page?: number
  limit?: number
}

// Category display helpers
export const CATEGORY_LABELS: Record<TicketCategory, string> = {
  bug: 'Bug Report',
  missing_image: 'Missing Image',
  feature_request: 'Feature Request',
  support: 'Support Request',
  data_issue: 'Data issue (catalog / card metadata)',
}

export const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed'
}

export const PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High'
}

/**
 * Create a new support ticket
 */
export const createTicket = async (data: CreateTicketData): Promise<Ticket> => {
  const response = await api.post('/support/tickets', data)
  return response.data.data
}

/**
 * List tickets with optional filters
 */
export const listTickets = async (filters: TicketFilters = {}): Promise<TicketListResponse> => {
  const params = new URLSearchParams()
  if (filters.status) params.append('status', filters.status)
  if (filters.category) params.append('category', filters.category)
  if (filters.priority) params.append('priority', filters.priority)
  if (filters.page) params.append('page', String(filters.page))
  if (filters.limit) params.append('limit', String(filters.limit))
  
  const response = await api.get(`/support/tickets?${params.toString()}`)
  return response.data.data
}

/**
 * Get ticket details by ID
 */
export const getTicket = async (ticketId: string): Promise<TicketDetail> => {
  const response = await api.get(`/support/tickets/${ticketId}`)
  return response.data.data
}

/**
 * Add a message to a ticket (chat-style)
 */
export const addMessage = async (ticketId: string, body: string): Promise<TicketMessage> => {
  const response = await api.post(`/support/tickets/${ticketId}/messages`, { body })
  return response.data.data
}

// Alias for backward compatibility
export const addComment = addMessage

// Quick report helpers
export interface QuickReportContext {
  cardId?: string
  cardName?: string
  setId?: string
  setName?: string
  imageUrl?: string
  page?: string
  userAgent?: string
  timestamp?: string
}

/**
 * Quick report: Missing image
 */
export const reportMissingImage = async (context: QuickReportContext): Promise<Ticket> => {
  const title = context.cardName 
    ? `Missing image for ${context.cardName}`
    : 'Missing card image reported'
  
  return createTicket({
    category: 'missing_image',
    priority: 'normal',
    title,
    description: `A missing or broken image was reported.\n\nCard: ${context.cardName || 'Unknown'}\nSet: ${context.setName || 'Unknown'}\nImage URL: ${context.imageUrl || 'N/A'}`,
    context: {
      ...context,
      reportedAt: new Date().toISOString()
    }
  })
}

/**
 * Quick report: Bug
 */
export const reportBug = async (
  title: string,
  description: string,
  context: QuickReportContext = {}
): Promise<Ticket> => {
  return createTicket({
    category: 'bug',
    priority: 'normal',
    title,
    description,
    context: {
      ...context,
      userAgent: navigator.userAgent,
      page: window.location.href,
      reportedAt: new Date().toISOString()
    }
  })
}

/**
 * Get unread ticket count for notifications
 */
export const getUnreadCount = async (): Promise<number> => {
  const response = await api.get('/support/unread-count', {
    headers: {
      'X-Client-Cache': true,
      'X-Client-Cache-Ttl': 30000
    }
  })
  return response.data.data.unreadCount
}

/**
 * Get unread ticket count for TCGEasy staff (super_admin)
 */
export const getAdminUnreadCount = async (): Promise<number> => {
  const response = await api.get('/support/admin/unread-count', {
    headers: {
      'X-Client-Cache': true,
      'X-Client-Cache-Ttl': 30000
    }
  })
  return response.data.data.unreadCount
}

export const supportService = {
  createTicket,
  listTickets,
  getTicket,
  addMessage,
  addComment,
  reportMissingImage,
  reportBug,
  getUnreadCount,
  getAdminUnreadCount,
  CATEGORY_LABELS,
  STATUS_LABELS,
  PRIORITY_LABELS
}

export default supportService
